/**
 * WorkspaceProvider — the whole screen's state layer (spec #44 §3, §4).
 *
 * THREE PROPERTIES ARE LOAD-BEARING, and each is pinned by a test:
 *
 * 1. ONE head poller for the whole workspace, never one per channel. The
 *    sidebar, the board's three lanes, the thread and the rail all read
 *    the same polled `head` through this context. A per-channel poller
 *    would multiply a 5-second loop by the roster size.
 * 2. THE SERVER OWNS THE CADENCE. `poll_after_ms` is honoured verbatim,
 *    read fresh at each tick (a `setTimeout` CHAIN, not `setInterval`, so
 *    the server can change cadence mid-session), clamped only by a
 *    1 000 ms runaway floor. The pattern is copy-adapted from
 *    `DataHealthContext` — single-flight ref, `visibilitychange` pause
 *    with an immediate refetch on return.
 * 3. THE CACHE IS TENANT-KEYED. `WorkspaceProvider` keys its inner state
 *    on `tenant_id`, so an operator switching tenants REMOUNTS it and
 *    every cached page, watermark and Ask transcript is discarded rather
 *    than re-labelled. Seen watermarks are namespaced per tenant in
 *    localStorage on top of that (§8 gate 5).
 *
 * Nothing here invents a value. A lane whose endpoint is missing or
 * failing gets an error string and renders an error state; a lane with no
 * records gets an empty state. Neither is ever a blank pane, and no
 * number is ever produced by this layer.
 */

import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  approveProposal,
  listActionProposals,
  rejectProposal,
} from '../services/actionProposalsApi';
import { getAgentTimeline, getTimelineHead } from '../services/agentTimelineApi';
import { listAgentSettings } from '../services/agentSettingsApi';
import { listHandoffs, resolveHandoff } from '../services/handoffsApi';
import { ask, listSpecialists } from '../services/marketingSuiteApi';
import { getPlan, getPlanBets } from '../services/planApi';
import { resolveTenantKey } from '../services/tenantIdentity';
import type { AgentSettings } from '../types/agentSettings';
import { HUMAN_HANDOFF_TYPES } from '../types/handoffs';
import type { HandoffItem, HandoffOutcome } from '../types/handoffs';
import type { ActionProposal } from '../types/actionProposals';
import type { Specialist } from '../types/marketingSuite';
import type { Bet, PlanResponse } from '../types/plan';
import type { TimelineEntry, TimelineHead } from '../types/timeline';
import type {
  AskTurn,
  PresenceState,
  WorkspaceQuestion,
} from '../types/workspace';
import {
  composeQuestions,
  inSeedOrder,
  specialistDomains,
} from './workspaceComposition';
import {
  channelUnreadCount,
  derivePresence,
  deriveUnread,
  readSeen,
  writeSeen,
} from './workspaceDerivations';

/** Fallback only — every real response carries the server's own cadence. */
const DEFAULT_POLL_AFTER_MS = 120_000;
/** Runaway floor. A server that says "poll in 5 ms" is a bug, not a
 * cadence, and this client will not melt a laptop over it. */
const MIN_POLL_AFTER_MS = 1_000;
/** The board's own floor: an expiry sweep changes proposals without a
 * cycle, so a still watermark is not proof the lane is current (§3). */
const BOARD_REFRESH_FLOOR_MS = 60_000;
/** One page is what a thread shows before "load more". */
const THREAD_PAGE_SIZE = 50;

export type LaneKey = 'rosters' | 'questions' | 'plan' | 'thread' | 'ask';

export interface WorkspaceContextValue {
  tenantKey: string;
  agents: AgentSettings[];
  specialists: Specialist[];
  head: TimelineHead | null;
  questions: WorkspaceQuestion[];
  plan: PlanResponse | null;
  bets: Bet[];
  loading: Record<LaneKey, boolean>;
  errors: Record<LaneKey, string | null>;
  /** The domain whose channel is open, from `?c={domain}`; `null` = board. */
  openDomain: string | null;
  openChannel: (domain: string | null) => void;
  entriesFor: (domain: string) => TimelineEntry[];
  /** `# team-now`: every agent's entries interleaved, newest first. */
  teamNowEntries: TimelineEntry[];
  loadTeamNow: () => Promise<void>;
  presenceFor: (domain: string) => PresenceState;
  unreadFor: (domain: string) => boolean;
  unreadCountFor: (domain: string) => number;
  needsYouCount: number;
  markSeen: (domain: string) => void;
  askTurnsFor: (domain: string) => AskTurn[];
  askAgent: (domain: string, question: string) => Promise<void>;
  askPending: boolean;
  /** Non-null when Ask is unavailable — the composer renders the reason. */
  askDisabledReason: string | null;
  approveQuestion: (question: WorkspaceQuestion) => Promise<void>;
  declineQuestion: (
    question: WorkspaceQuestion,
    reason?: string
  ) => Promise<void>;
  resolveQuestion: (
    question: WorkspaceQuestion,
    outcome: HandoffOutcome,
    reason?: string
  ) => Promise<void>;
  refreshBoard: () => Promise<void>;
}

export const WorkspaceContext = createContext<WorkspaceContextValue | null>(
  null
);

function message(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

interface ProviderProps {
  children: React.ReactNode;
  /** Overridable for tests and for an operator switch; defaults to the
   * tenant claim on the JWT the services already send. */
  tenantKey?: string;
}

/**
 * The public provider. Its only job is to KEY the real provider on the
 * tenant: React then remounts everything below on a switch, which is what
 * makes "no cached page survives" a structural guarantee rather than a
 * cleanup routine somebody has to remember to write.
 */
export function WorkspaceProvider({ children, tenantKey }: ProviderProps) {
  const resolved = tenantKey ?? resolveTenantKey();
  return (
    <WorkspaceProviderInner key={resolved} tenantKey={resolved}>
      {children}
    </WorkspaceProviderInner>
  );
}

function WorkspaceProviderInner({
  children,
  tenantKey,
}: {
  children: React.ReactNode;
  tenantKey: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const openDomain = searchParams.get('c');

  const [agents, setAgents] = useState<AgentSettings[]>([]);
  const [specialists, setSpecialists] = useState<Specialist[]>([]);
  const [head, setHead] = useState<TimelineHead | null>(null);
  // The lane's two SOURCES are held raw and the questions are derived, so
  // attribution recomputes when the specialist roster arrives — the two
  // requests race, and a proposal must not be stuck "not attributed"
  // because the catalog answered second.
  const [rawProposals, setRawProposals] = useState<ActionProposal[]>([]);
  const [rawHandoffs, setRawHandoffs] = useState<HandoffItem[]>([]);
  const [plan, setPlan] = useState<PlanResponse | null>(null);
  const [bets, setBets] = useState<Bet[]>([]);
  const [entries, setEntries] = useState<Record<string, TimelineEntry[]>>({});
  const [teamNowEntries, setTeamNowEntries] = useState<TimelineEntry[]>([]);
  const [askTurns, setAskTurns] = useState<Record<string, AskTurn[]>>({});
  const [askPending, setAskPending] = useState(false);
  const [askDisabledReason, setAskDisabledReason] = useState<string | null>(
    null
  );
  const [seen, setSeen] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState<Record<LaneKey, boolean>>({
    rosters: true,
    questions: true,
    plan: true,
    thread: false,
    ask: false,
  });
  const [errors, setErrors] = useState<Record<LaneKey, string | null>>({
    rosters: null,
    questions: null,
    plan: null,
    thread: null,
    ask: null,
  });

  const mountedRef = useRef(true);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Single-flight guards. Refs, not state: they must be readable and
  // writable synchronously inside one tick, which a setState cannot promise.
  const headInFlightRef = useRef(false);
  const boardInFlightRef = useRef(false);
  const threadInFlightRef = useRef<Set<string>>(new Set());
  const pollAfterMsRef = useRef(DEFAULT_POLL_AFTER_MS);
  const watermarkRef = useRef<string | null>(null);
  const lastBoardAtRef = useRef(0);

  const setLane = useCallback((lane: LaneKey, value: boolean) => {
    setLoading((prev) => ({ ...prev, [lane]: value }));
  }, []);

  const setLaneError = useCallback((lane: LaneKey, value: string | null) => {
    setErrors((prev) => ({ ...prev, [lane]: value }));
  }, []);

  // --- rosters -------------------------------------------------------------

  const loadRosters = useCallback(async () => {
    setLane('rosters', true);
    const [agentResult, specialistResult] = await Promise.allSettled([
      listAgentSettings(),
      listSpecialists(),
    ]);
    if (!mountedRef.current) return;
    if (agentResult.status === 'fulfilled') {
      setAgents(inSeedOrder(agentResult.value.agents ?? []));
      setLaneError('rosters', null);
    } else {
      setLaneError(
        'rosters',
        message(agentResult.reason, 'Could not load the agent roster')
      );
    }
    if (specialistResult.status === 'fulfilled') {
      setSpecialists(specialistResult.value.specialists ?? []);
    }
    setLane('rosters', false);
  }, [setLane, setLaneError]);

  // --- board lanes ---------------------------------------------------------

  /**
   * The three board lanes in one pass, single-flighted. Each source is
   * settled independently: a plan endpoint that has not landed yet must
   * not blank the questions lane beside it.
   */
  const loadBoard = useCallback(async () => {
    if (boardInFlightRef.current) return;
    boardInFlightRef.current = true;
    lastBoardAtRef.current = Date.now();
    setLane('questions', true);
    setLane('plan', true);
    try {
      const [proposals, handoffs, planResult, betsResult] =
        await Promise.allSettled([
          listActionProposals({
            status: 'proposed',
            tier: 'tier2_gated',
            limit: 50,
          }),
          listHandoffs({ status: 'open', types: HUMAN_HANDOFF_TYPES }),
          getPlan(),
          getPlanBets(),
        ]);
      if (!mountedRef.current) return;

      const proposalItems =
        proposals.status === 'fulfilled' ? proposals.value.proposals : [];
      const handoffItems: HandoffItem[] =
        handoffs.status === 'fulfilled' ? handoffs.value.items : [];

      if (proposals.status === 'rejected' && handoffs.status === 'rejected') {
        setLaneError(
          'questions',
          message(proposals.reason, 'Could not load open questions')
        );
        setRawProposals([]);
        setRawHandoffs([]);
      } else {
        setLaneError('questions', null);
        setRawProposals(proposalItems);
        setRawHandoffs(handoffItems);
      }

      if (planResult.status === 'fulfilled') {
        setPlan(planResult.value);
        setLaneError('plan', null);
      } else {
        setPlan(null);
        setLaneError(
          'plan',
          message(planResult.reason, 'Could not load the plan')
        );
      }
      setBets(betsResult.status === 'fulfilled' ? betsResult.value : []);
    } finally {
      boardInFlightRef.current = false;
      if (mountedRef.current) {
        setLane('questions', false);
        setLane('plan', false);
      }
    }
  }, [setLane, setLaneError]);

  const questions = useMemo(
    () =>
      composeQuestions(
        rawProposals,
        rawHandoffs,
        specialistDomains(specialists)
      ),
    [rawProposals, rawHandoffs, specialists]
  );

  // --- threads -------------------------------------------------------------

  /** One domain's page, fetched once per open and then served from cache. */
  const ensureTimeline = useCallback(
    async (domain: string, force = false) => {
      if (!domain) return;
      if (threadInFlightRef.current.has(domain)) return;
      if (!force && entriesRef.current[domain]) return;
      threadInFlightRef.current.add(domain);
      setLane('thread', true);
      try {
        const page = await getAgentTimeline(domain, {
          limit: THREAD_PAGE_SIZE,
        });
        if (!mountedRef.current) return;
        setEntries((prev) => ({ ...prev, [domain]: page.items }));
        setLaneError('thread', null);
      } catch (err) {
        if (mountedRef.current) {
          setLaneError('thread', message(err, 'Could not load this thread'));
        }
      } finally {
        threadInFlightRef.current.delete(domain);
        if (mountedRef.current) setLane('thread', false);
      }
    },
    [setLane, setLaneError]
  );

  const entriesRef = useRef<Record<string, TimelineEntry[]>>({});
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  /**
   * `# team-now` interleaves every agent's entries. The timeline contract
   * is per-domain (#43 §3), so this is one bounded request per configured
   * agent, issued only when that channel is opened — never on the board.
   */
  const loadTeamNow = useCallback(async () => {
    const domains = agents.filter((a) => a.configured).map((a) => a.domain);
    if (domains.length === 0) {
      setTeamNowEntries([]);
      return;
    }
    setLane('thread', true);
    const pages = await Promise.allSettled(
      domains.map((domain) => getAgentTimeline(domain, { limit: 25 }))
    );
    if (!mountedRef.current) return;
    const merged: TimelineEntry[] = [];
    pages.forEach((page) => {
      if (page.status === 'fulfilled') merged.push(...page.value.items);
    });
    merged.sort(
      (a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at)
    );
    setTeamNowEntries(merged);
    if (pages.every((p) => p.status === 'rejected')) {
      setLaneError('thread', 'Could not load the team timeline');
    } else {
      setLaneError('thread', null);
    }
    setLane('thread', false);
    // `agents` is a real dependency, not an incidental one: the roster
    // resolves AFTER the pane mounts, and a team view built from an empty
    // roster would render "no cycle has run yet" for a busy tenant.
  }, [agents, setLane, setLaneError]);

  // --- the single head poller ---------------------------------------------

  const pollHead = useCallback(async () => {
    if (headInFlightRef.current) return;
    headInFlightRef.current = true;
    try {
      const next = await getTimelineHead();
      if (!mountedRef.current) return;
      setHead(next);
      pollAfterMsRef.current = next.poll_after_ms || DEFAULT_POLL_AFTER_MS;
      const moved =
        Boolean(next.watermark) && next.watermark !== watermarkRef.current;
      const stale = Date.now() - lastBoardAtRef.current >= BOARD_REFRESH_FLOOR_MS;
      if (moved) {
        watermarkRef.current = next.watermark;
      }
      if (moved || stale) {
        await loadBoard();
      }
      if (moved) {
        // The pages a moved watermark can have changed: the open thread,
        // and the live tiles' one-liners. Both are already cached, so this
        // is a refresh of what is on screen — not a fan-out over the roster.
        const domain = openDomainRef.current;
        if (domain) await ensureTimeline(domain, true);
        await Promise.all(
          (next.active_agents ?? [])
            .filter((d) => d !== domain && entriesRef.current[d])
            .map((d) => ensureTimeline(d, true))
        );
      }
    } catch (err) {
      // A failed poll must never blank a screen the user is reading; the
      // next tick simply tries again.
      console.error('Failed to poll the workspace head:', err);
    } finally {
      headInFlightRef.current = false;
    }
  }, [ensureTimeline, loadBoard]);

  const openDomainRef = useRef<string | null>(openDomain);
  useEffect(() => {
    openDomainRef.current = openDomain;
  }, [openDomain]);

  const schedulePoll = useCallback(() => {
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    // Read the cadence FRESH at scheduling time — this is what makes
    // poll_after_ms server-owned rather than frozen at mount.
    const delay = Math.max(MIN_POLL_AFTER_MS, pollAfterMsRef.current);
    pollTimeoutRef.current = setTimeout(() => {
      void pollHead().then(() => {
        if (mountedRef.current && !document.hidden) schedulePoll();
      });
    }, delay);
  }, [pollHead]);

  // Mount: rosters + board + the first head, then start the chain.
  useEffect(() => {
    mountedRef.current = true;
    void loadRosters();
    void loadBoard();
    void pollHead().then(() => {
      if (mountedRef.current) schedulePoll();
    });
    return () => {
      mountedRef.current = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
    // Mount-only: the callbacks below are stable, and re-running this on
    // any of their identities changing would restart the poll chain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pause while hidden; refetch IMMEDIATELY on return.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
        return;
      }
      void pollHead().then(() => {
        if (mountedRef.current) schedulePoll();
      });
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [pollHead, schedulePoll]);

  // Opening a channel loads its page once and marks it seen.
  useEffect(() => {
    if (!openDomain) return;
    void ensureTimeline(openDomain);
  }, [openDomain, ensureTimeline]);

  /**
   * The live-work lane names what each ACTIVE agent is doing, and that
   * line is the agent's newest `TimelineEntry.title` — so the board reads
   * one page per agent with a cycle in flight, and no others. Bounded by
   * `active_agents`, which is normally nought to two.
   */
  const activeKey = (head?.active_agents ?? []).join(',');
  useEffect(() => {
    if (!activeKey) return;
    activeKey.split(',').forEach((domain) => {
      if (domain) void ensureTimeline(domain);
    });
  }, [activeKey, ensureTimeline]);

  // --- seen watermarks -----------------------------------------------------

  useEffect(() => {
    const domains = agents.map((a) => a.domain);
    if (domains.length === 0) return;
    setSeen((prev) => {
      const next = { ...prev };
      let changed = false;
      domains.forEach((domain) => {
        if (!(domain in next)) {
          next[domain] = readSeen(tenantKey, domain);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [agents, tenantKey]);

  const markSeen = useCallback(
    (domain: string) => {
      const at =
        headRef.current?.latest_at?.[domain] ??
        headRef.current?.watermark ??
        new Date().toISOString();
      writeSeen(tenantKey, domain, at);
      setSeen((prev) => ({ ...prev, [domain]: at }));
    },
    [tenantKey]
  );

  const headRef = useRef<TimelineHead | null>(null);
  useEffect(() => {
    headRef.current = head;
  }, [head]);

  // Opening a channel is what clears its dot.
  useEffect(() => {
    if (!openDomain || !head) return;
    markSeen(openDomain);
    // `head` intentionally in deps: an entry arriving while the channel is
    // open is read, not unread.
  }, [openDomain, head, markSeen]);

  // --- actions -------------------------------------------------------------

  const openChannel = useCallback(
    (domain: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (domain) next.set('c', domain);
      else next.delete('c');
      // A query param, not a route: the board stays the route and
      // back/forward work (§1.3).
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const refreshBoard = useCallback(async () => {
    await loadBoard();
  }, [loadBoard]);

  const approveQuestion = useCallback(
    async (question: WorkspaceQuestion) => {
      if (!question.proposal) return;
      await approveProposal(question.proposal.proposal_id);
      await loadBoard();
    },
    [loadBoard]
  );

  const declineQuestion = useCallback(
    async (question: WorkspaceQuestion, reason?: string) => {
      if (!question.proposal) return;
      await rejectProposal(question.proposal.proposal_id, reason);
      await loadBoard();
    },
    [loadBoard]
  );

  const resolveQuestion = useCallback(
    async (
      question: WorkspaceQuestion,
      outcome: HandoffOutcome,
      reason?: string
    ) => {
      if (!question.handoff) return;
      await resolveHandoff(question.handoff.id, outcome, reason);
      await loadBoard();
    },
    [loadBoard]
  );

  /**
   * The DM composer's one call (§2). L1: the question goes to the Ask
   * pipeline, which computes every number in the warehouse and CANNOT
   * author one, and the pair is held in memory for the session only —
   * the header line above the composer says exactly that.
   */
  const askAgent = useCallback(
    async (domain: string, question: string) => {
      const trimmed = question.trim();
      if (!trimmed) return;
      const id = `ask:${domain}:${Date.now()}`;
      const turn: AskTurn = {
        id,
        domain,
        question: trimmed,
        asked_at: new Date().toISOString(),
      };
      setAskTurns((prev) => ({
        ...prev,
        [domain]: [...(prev[domain] ?? []), turn],
      }));
      setAskPending(true);
      setLaneError('ask', null);
      try {
        const response = await ask(trimmed);
        if (!mountedRef.current) return;
        setAskTurns((prev) => ({
          ...prev,
          [domain]: (prev[domain] ?? []).map((t) =>
            t.id === id
              ? {
                  ...t,
                  answer_md: response.answer_md,
                  resolved_query: response.resolved_query,
                }
              : t
          ),
        }));
      } catch (err) {
        if (!mountedRef.current) return;
        const status = (err as { status?: number }).status;
        const code = (err as { code?: string }).code;
        const text = message(err, 'Ask failed');
        if (status === 402) {
          // Entitlement, not a bad question. The composer is REPLACED by
          // the plan's own reason, and the unanswerable turn is dropped —
          // leaving it would print the same sentence twice and imply the
          // question was asked and refused rather than never sent.
          setAskDisabledReason(text);
          setAskTurns((prev) => ({
            ...prev,
            [domain]: (prev[domain] ?? []).filter((t) => t.id !== id),
          }));
          return;
        }
        setAskTurns((prev) => ({
          ...prev,
          [domain]: (prev[domain] ?? []).map((t) =>
            t.id === id
              ? { ...t, error_code: code ?? String(status ?? 'error'), error_message: text }
              : t
          ),
        }));
      } finally {
        if (mountedRef.current) setAskPending(false);
      }
    },
    [setLaneError]
  );

  // --- derived reads -------------------------------------------------------

  const presenceFor = useCallback(
    (domain: string): PresenceState => {
      const agent = agents.find((a) => a.domain === domain);
      if (!agent) return 'not-set-up';
      return derivePresence(agent, head, questions);
    },
    [agents, head, questions]
  );

  const unreadFor = useCallback(
    (domain: string) =>
      deriveUnread(head?.latest_at?.[domain], seen[domain] ?? null),
    [head, seen]
  );

  const unreadCountFor = useCallback(
    (domain: string) =>
      channelUnreadCount(entries[domain] ?? [], seen[domain] ?? null),
    [entries, seen]
  );

  const entriesFor = useCallback(
    (domain: string) => entries[domain] ?? [],
    [entries]
  );

  const askTurnsFor = useCallback(
    (domain: string) => askTurns[domain] ?? [],
    [askTurns]
  );

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      tenantKey,
      agents,
      specialists,
      head,
      questions,
      plan,
      bets,
      loading,
      errors,
      openDomain,
      openChannel,
      entriesFor,
      teamNowEntries,
      loadTeamNow,
      presenceFor,
      unreadFor,
      unreadCountFor,
      needsYouCount: questions.length,
      markSeen,
      askTurnsFor,
      askAgent,
      askPending,
      askDisabledReason,
      approveQuestion,
      declineQuestion,
      resolveQuestion,
      refreshBoard,
    }),
    [
      tenantKey,
      agents,
      specialists,
      head,
      questions,
      plan,
      bets,
      loading,
      errors,
      openDomain,
      openChannel,
      entriesFor,
      teamNowEntries,
      loadTeamNow,
      presenceFor,
      unreadFor,
      unreadCountFor,
      markSeen,
      askTurnsFor,
      askAgent,
      askPending,
      askDisabledReason,
      approveQuestion,
      declineQuestion,
      resolveQuestion,
      refreshBoard,
    ]
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}
